/**
 * Errors slice (SCRUM-1950, RTK bundle 1 of 5): createSlice migration of the
 * legacy `errorReducer` switch. This is the GET_ERRORS hub: appErrorMiddleware
 * (SCRUM-840) and many action files dispatch the legacy string, and several
 * slices' extraReducers listen for it too, so the type strings stay exactly as
 * they are. `errorReducer.js` re-exports this reducer, so its importers do not
 * change. Pinned by src/tests/unit/rtkAppShell.characterisation.test.js.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { GET_ERRORS, CLEAR_ERRORS } from "./errorTypes";
import { NETWORK_ERROR_MESSAGE } from "../../utils/getErrorMessage";

/**
 * Whatever GET_ERRORS last carried: a server error body, a `{ message }`
 * envelope, a plain string, or `{}` / `null` for "cleared" — see
 * getErrorMessage for the shapes the hub reads.
 */
type ErrorsState = any;

const initialState: ErrorsState = {};

const errorsSlice = createSlice({
  name: "errors",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // A network error carries no response, so `err.response?.data` is undefined.
      // Returning undefined from an immer case keeps the old errors, and before
      // SCRUM-1960 the store threw on an undefined slice. SCRUM-1960 cleared it to
      // {} instead, which GlobalNotifications reads as "no error": the failure
      // vanished. Clearing is `CLEAR_ERRORS`, `{}` or `null`; an undefined payload
      // only comes from a failed request, so it is shown (SCRUM-2001).
      .addCase(GET_ERRORS, (_state, action: PayloadAction<ErrorsState, typeof GET_ERRORS>) =>
        action.payload === undefined
          ? { message: NETWORK_ERROR_MESSAGE }
          : action.payload
      )
      .addCase(CLEAR_ERRORS, () => ({}));
  },
});

export default errorsSlice.reducer;
